import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Image as ImageIcon, Pencil, Trash2, Upload } from 'lucide-react';
import { resolveColour, type CategoryColour } from '@fint/validation';
import { api, ApiError } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';
import { cn } from '@/lib/cn';
import {
  Button, Card, CardContent, CardHeader, CardTitle, ColourDot, ColourPicker, ErrorState, Input, Sheet, SheetBody,
  SheetFooter, SheetForm, SheetHeader, SkeletonTable, Textarea,
} from '@/features/common/ui';
import { LetterheadBlock } from '@/features/documents/LetterheadBlock';
import { addressLines, contactLine, paymentDetailRows, type BookAddress, type PaymentDetails } from '@/features/documents/document-layout';

/** `GET /v1/settings/book`: the book row, with the letterhead columns. */
interface Book extends BookAddress {
  id: string;
  name: string;
  colour: string;
  legalName?: string | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  logoFileId?: string | null;
  /** If the API names the image route itself, that wins over the one guessed below. */
  logoUrl?: string | null;
}

/** What the document needs from the settings blob. Every key optional: it lands beside this screen. */
interface LetterheadSettings {
  tax?: { gstin?: string | null } | null;
  defaultNotes?: string | null;
  defaultTerms?: string | null;
  letterhead?: Letterhead | null;
  paymentDetails?: PaymentDetails | null;
}

interface Letterhead {
  signatureFileId?: string | null;
  signatureUrl?: string | null;
  signatoryName?: string | null;
  showLogo?: boolean | null;
  accentColour?: string | null;
  tagline?: string | null;
}

/** Images ≤ 2 MB: the server's limit for a logo, checked here first so the message is instant. */
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
/** What the PDF engine can embed after the server's resize - `LETTERHEAD_IMAGE_MIME_TYPES`. No WebP, no HEIC. */
const IMAGE_TYPES = 'image/png,image/jpeg,image/gif';

/**
 * The bytes of a file the book owns - the same authenticated, org-scoped
 * route the receipts use, so there is no second, laxer path to a logo.
 */
const fileUrl = (orgId: string, fileId: string) => `/v1/files/${orgId}/${fileId}`;

/**
 * The `letterhead` object as the settings blob stores it, rebuilt whole.
 *
 * The settings PATCH replaces a nested object rather than merging it - see
 * OrgSettingsService.update - so every save sends every key it wants kept.
 * Empty strings and nulls are dropped rather than sent: a cleared tagline is
 * an absent tagline, and a schema that says `string().optional()` refuses a
 * null. `showLogo` is the one key always present; a boolean has no empty.
 */
function letterheadBody(current: Letterhead | null | undefined, overrides: Partial<Letterhead>): Record<string, unknown> {
  const merged = { ...current, ...overrides };
  const body: Record<string, unknown> = { showLogo: merged.showLogo !== false };
  for (const key of ['signatureFileId', 'signatoryName', 'accentColour', 'tagline'] as const) {
    const value = merged[key];
    if (typeof value === 'string' && value.trim()) body[key] = value.trim();
  }
  return body;
}

/**
 * The letterhead: logo, signature, the business's details, how to pay, and
 * the words at the foot of every invoice.
 *
 * ── WHY THE PREVIEW IS THE REAL COMPONENT ──
 * The card opens with the letterhead exactly as the document renders it -
 * `LetterheadBlock`, the same component, at the same proportions - so a
 * person uploading a logo sees where it lands and how big it prints before
 * a customer does. A mock-up drawn separately would be right until the
 * first time somebody changed the document and not the mock-up.
 *
 * ── SAVED PER SECTION ──
 * The business details live on the book row (`PATCH /settings/book`) and
 * everything else in the settings blob (`PATCH /settings`); they are edited
 * in separate sheets that each save their own thing, so a typo in a bank
 * account number cannot be refused by the address validation and vice
 * versa. The logo and signature save the moment they are chosen - a file
 * picker is its own confirmation - and "Show logo" saves on the tick.
 *
 * Read-only without `settings.manage`: the same summaries, no controls.
 */
export function LetterheadCard() {
  const org = useActiveOrg();
  const perms = usePermissions();
  const queryClient = useQueryClient();
  const canManage = perms.can('settings', 'manage');
  const [sheet, setSheet] = useState<'business' | 'payment' | 'defaults' | null>(null);

  const book = useQuery({
    queryKey: ['book', org?.id],
    queryFn: () => api.get<Book>('/settings/book'),
    enabled: !!org?.id,
  });
  const settings = useQuery({
    queryKey: ['settings', org?.id],
    queryFn: () => api.get<LetterheadSettings>('/settings'),
    enabled: !!org?.id,
  });

  /**
   * Everything that renders the letterhead is stale after a change here: the
   * switcher (the profile), this card, and any invoice document open in
   * another tab of the app.
   */
  const changed = () => {
    void queryClient.invalidateQueries({ queryKey: ['book', org?.id] });
    void queryClient.invalidateQueries({ queryKey: ['settings', org?.id] });
    void queryClient.invalidateQueries({ queryKey: ['profile'] });
    void queryClient.invalidateQueries({ queryKey: ['invoice-document', org?.id] });
    void queryClient.invalidateQueries({ queryKey: ['proforma-document', org?.id] });
  };

  const uploadImage = async (file: File, role: 'logo' | 'signature') => {
    if (!org) throw new Error('No book selected');
    if (file.size > MAX_IMAGE_BYTES) {
      throw new ApiError(400, `That image is ${(file.size / (1024 * 1024)).toFixed(1)} MB; a ${role} is limited to 2 MB.`);
    }
    return api.upload<{ id: string }>(`/files?ownerType=org&ownerId=${org.id}&role=${role}`, file);
  };

  /** The old file is deleted only AFTER the book stops pointing at it, and its failure is not the person's problem. */
  const discard = (fileId: string | null | undefined) => {
    if (fileId) void api.delete(`/files/${fileId}`).catch(() => undefined);
  };

  const logo = useMutation({
    mutationFn: async (file: File) => {
      const previous = book.data?.logoFileId;
      const uploaded = await uploadImage(file, 'logo');
      await api.patch('/settings/book', { logoFileId: uploaded.id });
      if (previous && previous !== uploaded.id) discard(previous);
    },
    onSuccess: changed,
  });
  const removeLogo = useMutation({
    mutationFn: async () => {
      const previous = book.data?.logoFileId;
      await api.patch('/settings/book', { logoFileId: null });
      discard(previous);
    },
    onSuccess: changed,
  });
  const signature = useMutation({
    mutationFn: async (file: File) => {
      const previous = settings.data?.letterhead?.signatureFileId;
      const uploaded = await uploadImage(file, 'signature');
      await api.patch('/settings', { letterhead: letterheadBody(settings.data?.letterhead, { signatureFileId: uploaded.id }) });
      if (previous && previous !== uploaded.id) discard(previous);
    },
    onSuccess: changed,
  });
  const removeSignature = useMutation({
    mutationFn: async () => {
      const previous = settings.data?.letterhead?.signatureFileId;
      await api.patch('/settings', { letterhead: letterheadBody(settings.data?.letterhead, { signatureFileId: null }) });
      discard(previous);
    },
    onSuccess: changed,
  });
  const showLogo = useMutation({
    mutationFn: (next: boolean) =>
      api.patch('/settings', { letterhead: letterheadBody(settings.data?.letterhead, { showLogo: next }) }),
    onSuccess: changed,
  });

  const message = (error: unknown) => (error instanceof ApiError ? error.message : error ? 'Something went wrong' : undefined);

  if (book.isError || settings.isError) {
    return (
      <Card data-testid="letterhead-card">
        <CardHeader>
          <CardTitle>Letterhead</CardTitle>
        </CardHeader>
        <ErrorState
          error={book.error ?? settings.error}
          onRetry={() => {
            void book.refetch();
            void settings.refetch();
          }}
        />
      </Card>
    );
  }

  const bookData = book.data;
  const letterhead = settings.data?.letterhead ?? undefined;
  const logoUrl = bookData?.logoUrl ?? (org && bookData?.logoFileId ? fileUrl(org.id, bookData.logoFileId) : null);
  const signatureUrl = letterhead?.signatureUrl ?? (org && letterhead?.signatureFileId ? fileUrl(org.id, letterhead.signatureFileId) : null);
  const logoShown = letterhead?.showLogo !== false;
  const accent = resolveColour(letterhead?.accentColour ?? org?.colour);
  const payment = paymentDetailRows(settings.data?.paymentDetails);
  const lines = addressLines(bookData);
  const contact = contactLine(bookData);

  return (
    <Card data-testid="letterhead-card">
      <CardHeader>
        <CardTitle>
          <ImageIcon className="size-4 text-muted-foreground" aria-hidden />
          Letterhead
        </CardTitle>
      </CardHeader>
      {book.isLoading || settings.isLoading || !bookData ? (
        <SkeletonTable rows={4} cols={2} />
      ) : (
        <CardContent className="space-y-5">
          <p className="text-sm text-muted-foreground">
            Your logo, business details and signature, on every invoice and proforma you send. The logo is kept in your
            own storage, beside your receipts.
          </p>

          {/* The preview: the same component the document prints, at the
              same proportions, on the same card surface. */}
          <div className="rounded-md border border-border bg-card p-3" data-testid="letterhead-preview" aria-label="Letterhead preview">
            <LetterheadBlock
              compact
              logoUrl={logoShown ? logoUrl : null}
              name={bookData.legalName?.trim() || org?.name || bookData.name}
              addressLines={lines}
              gstin={settings.data?.tax?.gstin}
              contact={contact}
              tagline={letterhead?.tagline}
              accent={accent}
              testId="letterhead-preview-block"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <ImageSlot
              title="Logo"
              hint="PNG, JPG or GIF up to 2 MB. Prints about 2 cm tall, top left."
              inputLabel="Upload a logo"
              imageUrl={logoUrl}
              imageClass="max-h-20 max-w-[60mm] object-contain"
              canManage={canManage}
              busy={logo.isPending || removeLogo.isPending}
              error={message(logo.error) ?? message(removeLogo.error)}
              onFile={(file) => logo.mutate(file)}
              onRemove={() => removeLogo.mutate()}
              testId="logo-slot"
            >
              {logoUrl ? (
                <div className="flex items-start gap-2 text-sm">
                  <input
                    id="letterhead-show-logo"
                    type="checkbox"
                    checked={logoShown}
                    disabled={!canManage || showLogo.isPending}
                    onChange={(event) => showLogo.mutate(event.target.checked)}
                    aria-describedby="letterhead-show-logo-hint"
                    className="mt-0.5"
                  />
                  <div>
                    <label htmlFor="letterhead-show-logo">Show logo on documents</label>
                    <p id="letterhead-show-logo-hint" className="text-xs text-muted-foreground">
                      Untick to keep the logo but print without it - on pre-printed stationery, say.
                    </p>
                  </div>
                </div>
              ) : null}
            </ImageSlot>

            <ImageSlot
              title="Signature"
              hint="A signature on a white background, up to 2 MB. Prints above “Authorized Signature”."
              inputLabel="Upload a signature"
              imageUrl={signatureUrl}
              imageClass="max-h-14 max-w-[45mm] object-contain"
              canManage={canManage}
              busy={signature.isPending || removeSignature.isPending}
              error={message(signature.error) ?? message(removeSignature.error)}
              onFile={(file) => signature.mutate(file)}
              onRemove={() => removeSignature.mutate()}
              testId="signature-slot"
            />
          </div>

          {showLogo.error ? (
            <p role="alert" className="text-sm text-destructive">
              {message(showLogo.error)}
            </p>
          ) : null}

          <Section
            title="Business details"
            canManage={canManage}
            onEdit={() => setSheet('business')}
            testId="business-details"
            summary={
              bookData.legalName?.trim() || lines.length || contact ? (
                <>
                  <p className="font-medium">{bookData.legalName?.trim() || org?.name}</p>
                  {lines.map((line) => (
                    <p key={line}>{line}</p>
                  ))}
                  {contact ? <p className="break-words">{contact}</p> : null}
                </>
              ) : (
                <p>Not set. The document will carry the book&rsquo;s name and nothing else.</p>
              )
            }
          />

          <Section
            title="Payment details"
            canManage={canManage}
            onEdit={() => setSheet('payment')}
            testId="payment-details"
            summary={
              payment.length ? (
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
                  {payment.map((row) => (
                    <div key={row.label} className="contents">
                      <dt className="text-muted-foreground">{row.label}</dt>
                      <dd className="break-words">{row.value}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <p>Not set. Bank and UPI details print at the foot of every invoice so a customer can pay it without asking.</p>
              )
            }
          />

          <Section
            title="Document defaults"
            canManage={canManage}
            onEdit={() => setSheet('defaults')}
            testId="document-defaults"
            summary={
              <>
                <p className="flex items-center gap-2">
                  <ColourDot colour={accent} className="h-2.5 w-2.5" />
                  <span>
                    Accent {accent[0].toUpperCase()}
                    {accent.slice(1)}
                    {letterhead?.accentColour ? '' : ' (the book colour)'}
                  </span>
                </p>
                {letterhead?.tagline?.trim() ? <p className="italic">{letterhead.tagline}</p> : null}
                <p>
                  Signed by {letterhead?.signatoryName?.trim() || <span className="text-muted-foreground">nobody named</span>}
                </p>
                <p className="text-muted-foreground">
                  Default notes {settings.data?.defaultNotes?.trim() ? 'set' : 'not set'} · default terms{' '}
                  {settings.data?.defaultTerms?.trim() ? 'set' : 'not set'}
                </p>
              </>
            }
          />

          {!canManage ? <p className="text-xs text-muted-foreground">Only an administrator can change these.</p> : null}
        </CardContent>
      )}

      {sheet === 'business' && bookData ? (
        <BusinessDetailsSheet
          book={bookData}
          onClose={() => setSheet(null)}
          onSaved={() => {
            changed();
            setSheet(null);
          }}
        />
      ) : null}
      {sheet === 'payment' ? (
        <PaymentDetailsSheet
          details={settings.data?.paymentDetails}
          onClose={() => setSheet(null)}
          onSaved={() => {
            changed();
            setSheet(null);
          }}
        />
      ) : null}
      {sheet === 'defaults' ? (
        <DocumentDefaultsSheet
          settings={settings.data}
          accent={accent}
          onClose={() => setSheet(null)}
          onSaved={() => {
            changed();
            setSheet(null);
          }}
        />
      ) : null}
    </Card>
  );
}

/** A titled summary with its Edit button - or without one, for a person who can only read. */
function Section({
  title,
  summary,
  canManage,
  onEdit,
  testId,
}: {
  title: string;
  summary: React.ReactNode;
  canManage: boolean;
  onEdit: () => void;
  testId: string;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-t border-border pt-4" data-testid={testId}>
      <div className="min-w-0 flex-1 space-y-0.5 text-sm">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{title}</p>
        {summary}
      </div>
      {canManage ? (
        // Named for what it edits, not just "Edit": three Edit buttons on
        // one card are three identical announcements.
        <Button variant="outline" size="sm" onClick={onEdit} aria-label={`Edit ${title.toLowerCase()}`}>
          <Pencil className="size-3.5" aria-hidden />
          Edit
        </Button>
      ) : null}
    </div>
  );
}

/**
 * One image the book keeps: a drop zone, the picture at the size it prints,
 * and Replace / Remove.
 *
 * The file input is visually hidden and driven by the Upload button, the
 * way the receipts strip does it; a drop anywhere in the zone is the other
 * way in. The `<img>` is the same URL the document uses, so a logo that
 * shows here is a logo the document can fetch.
 */
function ImageSlot({
  title,
  hint,
  inputLabel,
  imageUrl,
  imageClass,
  canManage,
  busy,
  error,
  onFile,
  onRemove,
  testId,
  children,
}: {
  title: string;
  hint: string;
  inputLabel: string;
  imageUrl: string | null;
  imageClass: string;
  canManage: boolean;
  busy: boolean;
  error?: string;
  onFile: (file: File) => void;
  onRemove: () => void;
  testId: string;
  children?: React.ReactNode;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const pick = (files: FileList | null) => {
    const first = files?.[0];
    if (first) onFile(first);
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      <p className="text-sm font-medium">{title}</p>
      <div
        onDragOver={(event) => {
          if (!canManage) return;
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          if (!canManage) return;
          event.preventDefault();
          setDragging(false);
          pick(event.dataTransfer.files);
        }}
        className={cn(
          'space-y-3 rounded-md border border-dashed p-3 transition-colors',
          dragging ? 'border-primary bg-primary/5' : 'border-border',
        )}
      >
        {imageUrl ? (
          // On paper, like the preview: a dark signature on the dark theme's
          // card was a black rectangle on navy.
          <img src={imageUrl} alt="" className={cn('rounded-sm bg-paper', imageClass)} data-testid={`${testId}-image`} />
        ) : (
          <p className="text-xs text-muted-foreground">{hint}</p>
        )}
        {canManage ? (
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={inputRef}
              type="file"
              className="sr-only"
              aria-label={inputLabel}
              accept={IMAGE_TYPES}
              onChange={(event) => {
                pick(event.target.files);
                // Cleared so choosing the same file twice fires onChange again.
                event.target.value = '';
              }}
            />
            <Button type="button" variant="outline" size="sm" loading={busy} onClick={() => inputRef.current?.click()}>
              <Upload className="size-3.5" aria-hidden />
              {imageUrl ? 'Replace' : 'Upload'}
            </Button>
            {imageUrl ? (
              <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={onRemove} aria-label={`Remove ${title.toLowerCase()}`}>
                <Trash2 className="size-3.5" aria-hidden />
                Remove
              </Button>
            ) : null}
            {imageUrl ? <p className="w-full text-xs text-muted-foreground">Drop a new file here to replace it.</p> : null}
          </div>
        ) : !imageUrl ? (
          <p className="text-xs text-muted-foreground">None yet.</p>
        ) : null}
        {children}
      </div>
      {error ? (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}

// ── The three sheets ──────────────────────────────────────────────────────

const BOOK_FIELDS = [
  'legalName',
  'addressLine1',
  'addressLine2',
  'city',
  'state',
  'postalCode',
  'country',
  'phone',
  'email',
  'website',
] as const;
type BookField = (typeof BOOK_FIELDS)[number];

/**
 * The business as it prints. Saved to the book row.
 *
 * A field the person emptied is sent as null so the column is cleared; a
 * field that was never set and is still empty is not sent at all, so the
 * PATCH says only what changed - and an untouched form sends nothing.
 */
function BusinessDetailsSheet({ book, onClose, onSaved }: { book: Book; onClose: () => void; onSaved: () => void }) {
  useEscapeToClose(onClose);
  const [values, setValues] = useState<Record<BookField, string>>(() =>
    Object.fromEntries(BOOK_FIELDS.map((field) => [field, book[field] ?? ''])) as Record<BookField, string>,
  );
  const set = (field: BookField) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setValues((current) => ({ ...current, [field]: event.target.value }));

  const save = useMutation({
    mutationFn: async () => {
      const patch: Record<string, string | null> = {};
      for (const field of BOOK_FIELDS) {
        const next = values[field].trim();
        const previous = (book[field] ?? '').trim();
        if (next === previous) continue;
        patch[field] = next || null;
      }
      if (Object.keys(patch).length === 0) return null;
      return api.patch('/settings/book', patch);
    },
    onSuccess: onSaved,
  });
  const error = save.error instanceof ApiError ? save.error : undefined;

  return (
    <Sheet labelledBy="business-details-title" onClose={onClose} testId="business-details-sheet">
      <SheetHeader titleId="business-details-title" title="Business details" onClose={onClose} />
      <SheetForm
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <SheetBody>
          <p className="text-sm text-muted-foreground">
            Printed top right of every document, under the logo. The GSTIN comes from the Tax card above.
          </p>
          <Input label="Legal name" value={values.legalName} onChange={set('legalName')} maxLength={200} autoFocus
            placeholder={book.name} error={error?.fieldError('legalName')} hint="As registered. Leave blank to print the book's name." />
          <Input label="Address line 1" value={values.addressLine1} onChange={set('addressLine1')} maxLength={200} error={error?.fieldError('addressLine1')} />
          <Input label="Address line 2" value={values.addressLine2} onChange={set('addressLine2')} maxLength={200} error={error?.fieldError('addressLine2')} />
          <div className="grid gap-4 [&>*]:min-w-0 sm:grid-cols-2">
            <Input label="City" value={values.city} onChange={set('city')} maxLength={100} error={error?.fieldError('city')} />
            <Input label="State" value={values.state} onChange={set('state')} maxLength={100} error={error?.fieldError('state')} />
            <Input label="PIN code" value={values.postalCode} onChange={set('postalCode')} maxLength={20} inputMode="numeric" error={error?.fieldError('postalCode')} />
            <Input label="Country" value={values.country} onChange={set('country')} maxLength={100} error={error?.fieldError('country')} />
          </div>
          <Input label="Phone" type="tel" value={values.phone} onChange={set('phone')} maxLength={40} error={error?.fieldError('phone')} />
          <Input label="Email" type="email" value={values.email} onChange={set('email')} maxLength={200} error={error?.fieldError('email')} />
          <Input label="Website" type="url" value={values.website} onChange={set('website')} maxLength={200}
            placeholder="https://" error={error?.fieldError('website')} hint="With its https://, or it is refused." />
          {error && !error.errors?.length ? (
            <p role="alert" className="text-sm text-destructive">
              {error.message}
            </p>
          ) : null}
        </SheetBody>
        <SheetFooter>
          <Button type="submit" loading={save.isPending}>
            Save business details
          </Button>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </SheetFooter>
      </SheetForm>
    </Sheet>
  );
}

const PAYMENT_FIELDS: Array<{ key: keyof PaymentDetails; label: string; hint?: string; upper?: boolean }> = [
  { key: 'bankName', label: 'Bank name' },
  { key: 'accountName', label: 'Account name', hint: 'The name the account is held in.' },
  { key: 'accountNumber', label: 'Account number' },
  { key: 'ifsc', label: 'IFSC', upper: true },
  { key: 'swift', label: 'SWIFT / BIC', upper: true, hint: 'For a customer paying from abroad. 8 or 11 characters.' },
  { key: 'branch', label: 'Branch' },
  { key: 'upiId', label: 'UPI ID', hint: 'name@bank - prints beside the bank details, with a scan-to-pay QR for the balance due.' },
];

/** How a customer pays. Saved to the settings blob as one object, blanks dropped. */
function PaymentDetailsSheet({
  details,
  onClose,
  onSaved,
}: {
  details: PaymentDetails | null | undefined;
  onClose: () => void;
  onSaved: () => void;
}) {
  useEscapeToClose(onClose);
  const [values, setValues] = useState<Record<keyof PaymentDetails, string>>(() => ({
    bankName: details?.bankName ?? '',
    accountName: details?.accountName ?? '',
    accountNumber: details?.accountNumber ?? '',
    ifsc: details?.ifsc ?? '',
    swift: details?.swift ?? '',
    branch: details?.branch ?? '',
    upiId: details?.upiId ?? '',
  }));

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, string> = {};
      for (const field of PAYMENT_FIELDS) {
        const value = values[field.key].trim();
        if (value) body[field.key] = field.upper ? value.toUpperCase() : value;
      }
      return api.patch('/settings', { paymentDetails: body });
    },
    onSuccess: onSaved,
  });
  const error = save.error instanceof ApiError ? save.error : undefined;

  return (
    <Sheet labelledBy="payment-details-title" onClose={onClose} testId="payment-details-sheet">
      <SheetHeader titleId="payment-details-title" title="Payment details" onClose={onClose} />
      <SheetForm
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <SheetBody>
          <p className="text-sm text-muted-foreground">
            Printed at the foot of every invoice, so a customer can pay it without asking how. Leave everything
            blank to print none.
          </p>
          {PAYMENT_FIELDS.map((field, index) => (
            <Input
              key={field.key}
              label={field.label}
              value={values[field.key]}
              onChange={(event) =>
                setValues((current) => ({
                  ...current,
                  [field.key]: field.upper ? event.target.value.toUpperCase() : event.target.value,
                }))
              }
              maxLength={80}
              autoFocus={index === 0}
              className={field.upper || field.key === 'accountNumber' ? 'font-mono' : undefined}
              hint={field.hint}
              error={error?.fieldError(`paymentDetails.${field.key}`)}
            />
          ))}
          {error && !error.errors?.length ? (
            <p role="alert" className="text-sm text-destructive">
              {error.message}
            </p>
          ) : null}
        </SheetBody>
        <SheetFooter>
          <Button type="submit" loading={save.isPending}>
            Save payment details
          </Button>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </SheetFooter>
      </SheetForm>
    </Sheet>
  );
}

/**
 * The words and the colour: tagline, who signs, the accent, and the notes
 * and terms every new document starts with. One save, one settings PATCH,
 * with the `letterhead` object rebuilt whole around the signature file it
 * already holds.
 */
function DocumentDefaultsSheet({
  settings,
  accent,
  onClose,
  onSaved,
}: {
  settings: LetterheadSettings | undefined;
  accent: CategoryColour;
  onClose: () => void;
  onSaved: () => void;
}) {
  useEscapeToClose(onClose);
  const [tagline, setTagline] = useState(settings?.letterhead?.tagline ?? '');
  const [signatoryName, setSignatoryName] = useState(settings?.letterhead?.signatoryName ?? '');
  const [colour, setColour] = useState<CategoryColour>(accent);
  const [notes, setNotes] = useState(settings?.defaultNotes ?? '');
  const [terms, setTerms] = useState(settings?.defaultTerms ?? '');

  const save = useMutation({
    mutationFn: () =>
      api.patch('/settings', {
        letterhead: letterheadBody(settings?.letterhead, { tagline, signatoryName, accentColour: colour }),
        defaultNotes: notes.trim(),
        defaultTerms: terms.trim(),
      }),
    onSuccess: onSaved,
  });
  const error = save.error instanceof ApiError ? save.error : undefined;

  return (
    <Sheet labelledBy="document-defaults-title" onClose={onClose} testId="document-defaults-sheet">
      <SheetHeader titleId="document-defaults-title" title="Document defaults" onClose={onClose} />
      <SheetForm
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <SheetBody>
          <Input label="Tagline" value={tagline} onChange={(event) => setTagline(event.target.value)} maxLength={120} autoFocus
            placeholder="Chartered Accountants since 1998" error={error?.fieldError('letterhead.tagline')}
            hint="One line under the address, on the letterhead." />
          <Input label="Signatory name" value={signatoryName} onChange={(event) => setSignatoryName(event.target.value)} maxLength={120}
            error={error?.fieldError('letterhead.signatoryName')} hint="Printed under the signature, above “Authorized Signature”." />
          <div className="space-y-1">
            <span className="text-sm font-medium">Accent colour</span>
            <p className="text-xs text-muted-foreground">The name, the title and the table head. The book colour unless you choose.</p>
            <ColourPicker label="Accent colour" value={colour} onChange={setColour} />
            {error?.fieldError('letterhead.accentColour') ? (
              <p className="text-xs text-destructive">{error.fieldError('letterhead.accentColour')}</p>
            ) : null}
          </div>
          <Textarea label="Default notes" value={notes} onChange={(event) => setNotes(event.target.value)} maxLength={4000}
            placeholder="Thank you for your business." error={error?.fieldError('defaultNotes')} />
          <Textarea label="Default terms" value={terms} onChange={(event) => setTerms(event.target.value)} maxLength={4000}
            placeholder="Payment within 15 days. Interest at 18% p.a. thereafter." error={error?.fieldError('defaultTerms')} />
          <p className="text-xs text-muted-foreground">
            Both are filled in on every new invoice and proforma, where they can be changed for that one document.
          </p>
          {error && !error.errors?.length ? (
            <p role="alert" className="text-sm text-destructive">
              {error.message}
            </p>
          ) : null}
        </SheetBody>
        <SheetFooter>
          <Button type="submit" loading={save.isPending}>
            Save document defaults
          </Button>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </SheetFooter>
      </SheetForm>
    </Sheet>
  );
}
